import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Flex, Group, Button, ActionIcon, Tooltip, Text, Alert } from '@mantine/core';
import Ellipsis from 'lucide-react/dist/esm/icons/ellipsis';
import { useAuth } from '../../shared/AuthContext';
import { usePageTitle } from '../../shared/utils';
import { getLastDir, setLastDir, type ShareId } from '../../shared/lastDir';
import { useEditorWebSocket } from './hooks/useEditorWebSocket';
import { useVideoStreamer } from './hooks/useVideoStreamer';
import { useUndoRedo } from './hooks/useUndoRedo';
import { FilePickerModal } from './components/FilePickerModal';
import { SegmentCard } from './components/SegmentCard';
import { MediaBin } from './components/MediaBin';
import { VideoPlayer } from './components/VideoPlayer';
import { Timeline } from './components/Timeline';
import { EditorSettings } from './components/EditorSettings';
import { LogPanel } from './components/LogPanel';
import type { BrowseResult, BrowseTarget, ClipData, GlobalSettings, Segment, OverlayConfig, MaskConfig, MediaBinItem, DisplayBlock, LogEntry, LogLevel, LogKind, HwCapabilities, OverallProgress, SilenceInterval } from './types';
import { formatClock, formatTime, makeSegment, getSegmentOffsets, makeDefaultOverlay, newSegId, normalizeSegment, normalizeCrop, parseTime, MAX_SEGMENTS, getSegmentOverlays, getSegmentMasks, getPipSnapCoords, PIP_DEFAULT_W, type PipCorner } from './utils';

function fitCanvasSize(w: number, h: number): { width: number; height: number } {
  const width = Math.min(w, 1920) & ~1;
  return { width, height: Math.round(width / (w / h)) & ~1 };
}

export function Editor() {
  usePageTitle('Автомонтаж');
  const auth = useAuth();

  const [mediaBin, setMediaBin] = useState<MediaBinItem[]>([]);
  const [segments, setSegments] = useState<Segment[]>([makeSegment()]);
  const [settings, setSettings] = useState<GlobalSettings>(() => ({
    useIntro: true, outputDir: '', outputFilename: 'fin', volume: 1.0, deadlineHours: 24,
    canvasWidth: 1920, canvasHeight: 1080, canvasFps: 60, x264Preset: 'medium',
  }));
  const [canvasWarnDismissed, setCanvasWarnDismissed] = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [hwCapabilities, setHwCapabilities] = useState<HwCapabilities | null>(null);
  const [defaultIntro, setDefaultIntro] = useState('');
  const [clips, setClips] = useState<ClipData[]>([]);
  const [totalDuration, setTotalDuration] = useState(0);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [logEntries, setLogEntries] = useState<LogEntry[]>([]);
  const [logLevel, setLogLevel] = useState<LogLevel>('info');
  const [isProcessing, setIsProcessing] = useState(false);
  const [overallProgress, setOverallProgress] = useState<OverallProgress | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);

  const [currentTime, setCurrentTime] = useState(0);
  const [selectedSegId, setSelectedSegId] = useState<string | null>(null);
  const [activePipSegId, setActivePipSegId] = useState<string | null>(null);
  const [waveforms, setWaveforms] = useState<Record<string, number[]>>({});
  const [silenceMap, setSilenceMap] = useState<Record<string, SilenceInterval[]>>({});
  const [isCropping, setIsCropping] = useState(false);

  // Состояние замазки (маскирования)
  const [isMasking, setIsMasking] = useState(false);
  const [activeMaskId, setActiveMaskId] = useState<string | null>(null);
  const [detectingMaskId, setDetectingMaskId] = useState<string | null>(null);

  const [browseTarget, setBrowseTarget] = useState<BrowseTarget | null>(null);
  const [browseResult, setBrowseResult] = useState<BrowseResult | null>(null);

  const fileImportRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const pipVideoRef = useRef<HTMLVideoElement>(null);
  const previewTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showFirstFrameRef = useRef(true);
  const requestedWaveforms = useRef<Set<string>>(new Set());
  const hasAutoAppliedCanvasRef = useRef(false);

  const projectHistoryState = useMemo(() => ({ segments, settings, mediaBin }), [segments, settings, mediaBin]);
  const restoreProjectHistory = useCallback((st: { segments: Segment[]; settings: GlobalSettings; mediaBin: MediaBinItem[] }) => {
    setSegments(st.segments); setSettings(st.settings); setMediaBin(st.mediaBin);
  }, []);
  const { undo: handleUndo, redo: handleRedo } = useUndoRedo(projectHistoryState, restoreProjectHistory);

  const gatherParams = useCallback(() => ({
    use_ram: true, intro_file: settings.useIntro ? defaultIntro : '',
    output_dir: settings.outputDir, output_filename: (settings.outputFilename.trim() || 'fin') + '.mp4',
    volume: settings.volume, deadline_hours: settings.deadlineHours,
    canvas_width: settings.canvasWidth, canvas_height: settings.canvasHeight, canvas_fps: settings.canvasFps,
    x264_preset: settings.x264Preset || 'medium', encoder_type: settings.encoderType || (hwCapabilities?.can_gpu_encode ? 'gpu' : 'cpu'),
    gpu_qp: settings.gpuQp ?? 18, gpu_compression_level: settings.gpuCompressionLevel ?? 1, gpu_bframes: settings.gpuBframes ?? 3,
    normalize_loudness: Boolean(settings.normalizeLoudness),
    segments_list: segments.filter(s => s.video),
  }), [settings, defaultIntro, segments, hwCapabilities]);

  const handleWsMessage = useCallback((data: any) => {
    switch (data.action) {
      case 'config_info':
        requestedWaveforms.current.clear();
        if (data.default_intro) setDefaultIntro(data.default_intro);
        if (data.hw_capabilities) setHwCapabilities(data.hw_capabilities);
        browse('source');
        break;
      case 'preview_map_ready': {
        const dur = (data.total_duration as number) || 0;
        setTotalDuration(dur); setClips((data.clips as ClipData[]) || []);
        streamer.setTotalDuration(dur); streamer.setFragmentDuration(data.fragment_duration ?? 10); streamer.setClips(data.clips || []);
        setPreviewVisible(dur > 0);
        if (showFirstFrameRef.current && dur > 0) { showFirstFrameRef.current = false; streamer.refresh(0); }
        else streamer.refresh();
        break;
      }
      case 'preview_fragment_ready': streamer.onChunkReady(data); break;
      case 'preview_fragment_error': streamer.onChunkError(data); break;
      case 'browse_result': setBrowseResult({ path: data.path, entries: data.entries }); setLastDir(browseShareRef.current, data.path); break;
      case 'path_resolved':
        if (browseTarget) { applyResolvedPath(browseTarget, data.full_path); setBrowseTarget(null); }
        break;
      case 'waveform_data':
        if (data.path) requestedWaveforms.current.delete(data.path);
        if (data.real_path) requestedWaveforms.current.delete(data.real_path);
        if (data.path && Array.isArray(data.peaks)) {
          setWaveforms(prev => ({ ...prev, [data.path]: data.peaks, ...(data.real_path ? { [data.real_path]: data.peaks } : {}) }));
        }
        if (data.path && Array.isArray(data.silence)) {
          setSilenceMap(prev => ({ ...prev, [data.path]: data.silence, ...(data.real_path ? { [data.real_path]: data.silence } : {}) }));
        }
        break;
      case 'slide_bounds_result': {
        const { seg_id, mask_id, start_sec, end_sec } = data;
        setDetectingMaskId(null);
        setSegments(prev => prev.map(s => {
          if (s.id !== seg_id) return s;
          const msks = getSegmentMasks(s).map(m => {
            if (m.id !== mask_id) return m;
            return {
              ...m,
              start: formatTime(start_sec),
              end: formatTime(end_sec),
            };
          });
          return { ...s, masks: msks };
        }));
        break;
      }
      case 'auto_color_result': {
        const { seg_id, ov_id, lift, gamma, gain } = data;
        setSegments(prev => prev.map(s => {
          if (s.id !== seg_id) return s;
          const curOvs = getSegmentOverlays(s);
          const target = ov_id ? curOvs.find(o => o.id === ov_id) : curOvs[0];
          const targetId = target ? target.id : curOvs[0]?.id;
          const updated = curOvs.map(o => o.id !== targetId ? o : { ...o, color_enabled: true, lift, gamma, gain });
          return { ...s, overlays: updated, overlay: updated[0] || null };
        }));
        break;
      }
      case 'auto_sync_result': {
        const { seg_id, ov_id, offset, sample_path, sample_duration } = data;
        const camDur = (typeof sample_duration === 'number' && sample_duration > 0) ? sample_duration : (mediaBin.find(x => x.path === sample_path)?.duration || 1790.37);
        const [tlStart, camStart] = offset < 0 ? [0, Math.abs(offset)] : [offset, 0];
        const [tlEnd, camEnd] = offset < 0 ? [Math.max(0, camDur - camStart), camDur] : [offset + camDur, camDur];

        setSegments(prev => prev.map(s => {
          if (s.id !== seg_id) return s;
          const curOvs = getSegmentOverlays(s);
          const target = ov_id ? curOvs.find(o => o.id === ov_id) : curOvs.find(o => o.video === sample_path);
          const targetId = target ? target.id : curOvs[0]?.id;
          const updated = curOvs.map(o => o.id !== targetId ? o : { ...o, start: formatTime(camStart), end: formatTime(camEnd), timeline_start: formatTime(tlStart), timeline_end: formatTime(tlEnd) });
          return { ...s, overlays: updated, overlay: updated[0] || null };
        }));
        break;
      }
      case 'duration_resolved': {
        const { seg_id: segId, duration: d, field, width, height, fps, is_vfr, avg_fps } = data;
        if (field === 'bin') {
          setMediaBin(prev => prev.map(x => (x.id === segId || x.path === segId) ? { ...x, duration: d, width, height, fps, is_vfr, avg_fps } : x));
        } else if (d > 0) {
          setSegments(prev => prev.map(s => {
            if (s.id !== segId) return s;
            if (field === 'pip_end' && s.overlay && !s.overlay.end) return { ...s, overlay: { ...s.overlay, end: formatTime(d) } };
            return !s.end ? { ...s, end: formatTime(d) } : s;
          }));
        }
        break;
      }
      case 'log':
        setLogEntries(prev => {
          const entry = { level: (data.level ?? 'info') as LogLevel, kind: (data.kind ?? 'line') as LogKind, source: (data.source ?? (data.kind === 'ffmpeg' ? 'ffmpeg' : 'system')) as 'system' | 'ffmpeg', message: data.message ?? '', ts: formatClock(), stage: data.stage as string | undefined };
          const base = data.kind === 'progress' ? prev.filter(e => e.kind !== 'progress' || (data.stage !== undefined && e.stage !== data.stage)) : prev;
          return base.length >= 10000 ? [...base.slice(base.length - 9999), entry] : [...base, entry];
        });
        if (data.rendered_time && data.total_time) setOverallProgress({ rendered: data.rendered_time, total: data.total_time, percent: data.percent ?? 0 });
        if (data.message && data.message.includes('Task complete')) setOverallProgress(prev => prev ? { ...prev, rendered: prev.total, percent: 100 } : null);
        break;
      case 'finished':
        setIsProcessing(false); setIsCancelling(false);
        setOverallProgress(prev => prev ? { ...prev, rendered: prev.total, percent: 100 } : null);
        break;
    }
  }, [browseTarget, mediaBin]);

  const { send } = useEditorWebSocket(handleWsMessage, useCallback(async () => (await auth.identity()).token, [auth]));
  const browseShareRef = useRef<ShareId>('source');
  const browse = (share: ShareId, path = getLastDir(share), mode?: 'audio') => { browseShareRef.current = share; send({ action: 'browse_path', share, path, mode }); };

  useEffect(() => {
    const paths = new Set<string>();
    for (const s of segments) {
      if (s.audio || s.video) paths.add(s.audio || s.video);
      getSegmentOverlays(s).forEach(o => { if (o.video) paths.add(o.video); });
    }
    if (settings.useIntro && defaultIntro) paths.add(defaultIntro);
    paths.forEach(path => {
      if (!waveforms[path] && !requestedWaveforms.current.has(path)) {
        requestedWaveforms.current.add(path);
        send({ action: 'get_waveform', path });
      }
    });
  }, [segments, settings.useIntro, defaultIntro, waveforms, send]);

  const timelineSignature = useMemo(() => segments.map(s => {
    const ovs = getSegmentOverlays(s).map(o => `${o.id}:${o.video}:${o.start}:${o.end}:${o.timeline_start}:${o.timeline_end}:${o.mix_audio}`).join(';');
    return `${s.id}:${s.video}:${s.audio || s.video}:${s.start}:${s.end}:${s.fade_in}:${s.fade_out}:${s.volume}:${ovs}`;
  }).join('|') + `|vol:${settings.volume}|nl:${settings.normalizeLoudness}`, [segments, settings.volume, settings.normalizeLoudness]);

  const gatherParamsRef = useRef(gatherParams); gatherParamsRef.current = gatherParams;
  useEffect(() => {
    if (!segments.some(s => Boolean(s.video)) && !settings.useIntro) {
      setPreviewVisible(false); setTotalDuration(0); setClips([]); return;
    }
    if (previewTimer.current) clearTimeout(previewTimer.current);
    previewTimer.current = setTimeout(() => send({ action: 'generate_preview_map', params: gatherParamsRef.current() }), 50);
    return () => { if (previewTimer.current) clearTimeout(previewTimer.current); };
  }, [timelineSignature, settings.useIntro, settings.normalizeLoudness, defaultIntro, send]);

  const streamer = useVideoStreamer({
    videoRef, pipVideoRef,
    onRequestFragment: useCallback((time, reason, isPreload, reqId) => send({ action: 'generate_preview_fragment', start_time: time, exact_time: time, is_preload: isPreload, request_id: reqId, params: gatherParams() }), [send, gatherParams]),
    onFetchBytes: useCallback(async (relPath) => {
      const { token } = await auth.identity();
      const rsp = await fetch(`/api/editor/video?path=${encodeURIComponent(relPath)}&t=${Date.now()}`, { headers: { authorization: `Bearer ${token}` } });
      if (!rsp.ok) throw new Error('Fetch failed');
      return rsp.arrayBuffer();
    }, [auth]),
    onReportTiming: useCallback(r => send({ action: 'preview_timing', request_id: r.requestId, wait_ms: r.waitMs, transfer_ms: r.transferMs }), [send]),
    onTimeUpdate: useCallback((t, total) => { setCurrentTime(t); setTotalDuration(total); }, []),
    onPipChange: useCallback(segId => setActivePipSegId(segId), []),
  });

  useEffect(() => { streamer.init(); return () => streamer.destroy(); }, [streamer.init, streamer.destroy]);

  useEffect(() => {
    if (segments.length && !segments.some(s => s.id === selectedSegId)) setSelectedSegId(segments[0].id);
  }, [segments, selectedSegId]);

  const prevSelectedRef = useRef<string | null>(null);
  useEffect(() => {
    const prev = prevSelectedRef.current; prevSelectedRef.current = selectedSegId;
    if (!prev || prev === selectedSegId) return;
    setSegments(segs => (segs.find(s => s.id === prev && !s.video) && segs.length > 1) ? segs.filter(s => s.id !== prev) : segs);
  }, [selectedSegId]);

  const addPathToBin = useCallback((path: string) => {
    if (!path) return;
    setMediaBin(prev => prev.some(x => x.path === path) ? prev : [...prev, { id: newSegId(), path, name: path.split('/').pop() || path }]);
    send({ action: 'resolve_duration', path, seg_id: path, field: 'bin' });
  }, [send]);

  function applyResolvedPath(target: BrowseTarget, path: string) {
    if (target.type === 'output') { setSettings(s => ({ ...s, outputDir: path })); return; }
    addPathToBin(path);
    if (target.type === 'video') {
      setSegments(prev => prev.map(s => s.id === target.segId ? { ...s, video: path, audio: path, end: s.video === path ? s.end : '' } : s));
      showFirstFrameRef.current = true;
      send({ action: 'resolve_duration', path, seg_id: target.segId, field: 'end' });
      send({ action: 'get_waveform', path });
    } else if (target.type === 'audio') {
      setWaveforms(prev => { const n = { ...prev }; delete n[path]; return n; });
      setSegments(prev => prev.map(s => s.id === target.segId ? { ...s, audio: path } : s));
      send({ action: 'get_waveform', path });
    } else if (target.type === 'overlay') {
      setSegments(prev => prev.map(s => s.id !== target.segId ? s : { ...s, overlay: s.overlay ? { ...s.overlay, video: path, end: s.overlay.video === path ? s.overlay.end : '' } : makeDefaultOverlay(path) }));
      showFirstFrameRef.current = true;
      send({ action: 'resolve_duration', path, seg_id: target.segId, field: 'pip_end' });
    }
  }

  function updateSegment(id: string, patch: Partial<Segment>) {
    if (patch.audio) { setWaveforms(prev => { const n = { ...prev }; delete n[patch.audio!]; return n; }); send({ action: 'get_waveform', path: patch.audio }); }
    setSegments(prev => prev.map(s => s.id === id ? { ...s, ...patch } : s));
  }

  function removeSegment(id: string) {
    setSelectedSegId(sel => {
      if (sel !== id) return sel;
      const idx = segments.findIndex(s => s.id === id);
      const rest = segments.filter(s => s.id !== id);
      return rest[Math.min(idx, rest.length - 1)]?.id ?? null;
    });
    setSegments(prev => prev.filter(s => s.id !== id));
  }

  function splitSegment(segId: string, globalTime: number) {
    const s = segments.find(x => x.id === segId);
    const own = clips.filter(c => c.segment_id === segId);
    if (!s || !own.length) return;
    const ownStart = Math.min(...own.map(c => c.global_start));
    const off = globalTime - ownStart;
    if (off <= 0.05 || off >= own.reduce((sum, c) => sum + c.duration, 0) - 0.05) return;

    const splitSec = parseTime(s.start) + off;
    const splitStr = formatTime(splitSec);
    const ovs1: OverlayConfig[] = [], ovs2: OverlayConfig[] = [];
    const msks1: MaskConfig[] = [], msks2: MaskConfig[] = [];

    getSegmentOverlays(s).forEach(ov => {
      const tlS = parseTime(ov.timeline_start || s.start || '00:00:00');
      const tlE = parseTime(ov.timeline_end || s.end || '99:99:99');
      const camS = parseTime(ov.start || '00:00:00');
      if (tlE <= splitSec) ovs1.push({ ...ov });
      else if (tlS >= splitSec) ovs2.push({ ...ov, id: newSegId() });
      else {
        ovs1.push({ ...ov, timeline_end: splitStr, end: formatTime(camS + splitSec - tlS) });
        ovs2.push({ ...ov, id: newSegId(), timeline_start: splitStr, start: formatTime(camS + splitSec - tlS) });
      }
    });

    getSegmentMasks(s).forEach(m => {
      const mS = parseTime(m.start);
      const mE = parseTime(m.end || '99:99:99');
      if (mE <= splitSec) msks1.push({ ...m });
      else if (mS >= splitSec) msks2.push({ ...m, id: newSegId() });
      else {
        msks1.push({ ...m, end: splitStr });
        msks2.push({ ...m, id: newSegId(), start: splitStr });
      }
    });

    const sEndStr = s.end || (mediaBin.find(x => x.path === s.video)?.duration ? formatTime(mediaBin.find(x => x.path === s.video)!.duration!) : '');
    const s1: Segment = { ...s, id: newSegId(), end: splitStr, fade_out: false, overlays: ovs1, overlay: ovs1[0] || null, masks: msks1 };
    const s2: Segment = { ...s, id: newSegId(), start: splitStr, end: sEndStr, fade_in: false, overlays: ovs2, overlay: ovs2[0] || null, masks: msks2 };

    setSegments(prev => {
      const idx = prev.findIndex(x => x.id === segId);
      const next = [...prev]; next.splice(idx, 1, s1, s2); return next;
    });
    setSelectedSegId(s1.id);
  }

  const splitAtPlayhead = useCallback(() => {
    const active = clips.find(c => c.segment_id && currentTime >= c.global_start && currentTime < c.global_start + c.duration);
    if (active?.segment_id) splitSegment(active.segment_id, currentTime);
    else if (selectedSegId) splitSegment(selectedSegId, currentTime);
  }, [clips, currentTime, selectedSegId, splitSegment]);

  const ctRef = useRef(currentTime); ctRef.current = currentTime;
  const tdRef = useRef(totalDuration); tdRef.current = totalDuration;

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      const t = e.target as HTMLElement | null;
      if (t instanceof HTMLInputElement || t instanceof HTMLTextAreaElement || t?.isContentEditable || isMasking) return;
      const mod = e.ctrlKey || e.metaKey;
      if (mod && e.code === 'KeyZ' && !e.shiftKey) { e.preventDefault(); handleUndo(); return; }
      if ((mod && e.code === 'KeyZ' && e.shiftKey) || (mod && e.code === 'KeyY')) { e.preventDefault(); handleRedo(); return; }
      if (mod || e.altKey) return;
      if (e.code === 'Space') { e.preventDefault(); streamer.togglePlay(); return; }
      if (['KeyA', 'ArrowLeft'].includes(e.code) || ['ф', 'Ф'].includes(e.key)) { e.preventDefault(); streamer.seek(Math.max(0, ctRef.current - 5)); return; }
      if (['KeyD', 'ArrowRight'].includes(e.code) || ['в', 'В'].includes(e.key)) { e.preventDefault(); streamer.seek(Math.min(tdRef.current, ctRef.current + 5)); return; }
      if (e.code === 'KeyS' || ['ы', 'Ы'].includes(e.key)) { e.preventDefault(); splitAtPlayhead(); }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [handleUndo, handleRedo, streamer, splitAtPlayhead]);

  function reorderSegment(from: number, to: number) {
    if (from === to || from < 0 || from >= segments.length) return;
    setSegments(prev => {
      const next = [...prev]; const [moved] = next.splice(from, 1);
      next.splice(Math.max(0, Math.min(to, next.length)), 0, moved); return next;
    });
  }

  function addSegmentFromBin(path: string) {
    if (segments.length >= MAX_SEGMENTS) return;
    const item = mediaBin.find(x => x.path === path);
    const newSeg = makeSegment(path);
    if (item?.duration) newSeg.end = formatTime(item.duration);
    setSegments(prev => (prev.length === 1 && !prev[0].video) ? [newSeg] : [...prev, newSeg]);
    setSelectedSegId(newSeg.id); showFirstFrameRef.current = true;
    if (!item?.duration) send({ action: 'resolve_duration', path, seg_id: newSeg.id, field: 'end' });
  }

  const handleExport = useCallback(() => {
    const exportData = {
      version: 1, exported_at: new Date().toISOString(), settings, mediaBin,
      segments: segments.map(s => ({
        ...s,
        crop: normalizeCrop(s.crop),
        overlay: s.overlay ? { ...s.overlay, crop: normalizeCrop(s.overlay.crop) } : null,
        overlays: getSegmentOverlays(s).map(o => ({ ...o, crop: normalizeCrop(o.crop) })),
        masks: getSegmentMasks(s),
      }))
    };
    const url = URL.createObjectURL(new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' }));
    const a = document.createElement('a'); a.href = url; a.download = `${settings.outputFilename || 'project'}_timeline.json`; a.click();
    URL.revokeObjectURL(url);
  }, [settings, mediaBin, segments]);

  const handleImport = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const data = JSON.parse(text);

      // Поддерживаем как полноценный экспорт проекта, так и сырой массив сегментов
      let rawSegs: any[] = [];
      if (Array.isArray(data)) {
        rawSegs = data;
      } else if (Array.isArray(data.segments)) {
        rawSegs = data.segments;
      } else if (Array.isArray(data.segments_list)) {
        rawSegs = data.segments_list;
      } else if (Array.isArray(data.timeline)) {
        rawSegs = data.timeline;
      } else if (data.project && Array.isArray(data.project.segments)) {
        rawSegs = data.project.segments;
      }

      const imported = rawSegs.map(normalizeSegment);

      // Применяем настройки
      const newSettings = data.settings || data.project?.settings || {};
      if (newSettings && Object.keys(newSettings).length > 0) {
        setSettings(s => ({ ...s, ...newSettings }));
        if (newSettings.canvasWidth) hasAutoAppliedCanvasRef.current = true;
      }

      if (imported.length > 0) {
        setSegments(imported);
        setSelectedSegId(imported[0].id);
      } else if (!newSettings || Object.keys(newSettings).length === 0) {
        alert('В выбранном файле JSON не найдено ни одного сегмента или настройки!');
        return;
      }

      // Собираем все уникальные пути файлов (видео, внешнее аудио, оверлеи) в Корзину
      const rawPaths: string[] = imported.flatMap((s: Segment) => [
        s.video,
        s.audio,
        s.overlay?.video,
        ...(s.overlays || []).map(o => o.video)
      ]).filter((p): p is string => Boolean(p));

      const existingBin = Array.isArray(data.mediaBin) ? data.mediaBin : [];
      const allPaths: string[] = Array.from(new Set([...existingBin.map((x: any) => x.path), ...rawPaths])).filter((p): p is string => Boolean(p));
      const bin: MediaBinItem[] = allPaths.map(path => {
        const found = existingBin.find((x: any) => x.path === path);
        return {
          id: found?.id || newSegId(),
          path,
          name: found?.name || path.split('/').pop() || path,
          duration: found?.duration,
          width: found?.width,
          height: found?.height,
          fps: found?.fps
        };
      });

      setMediaBin(bin);

      // Запрашиваем метаданные и звуковые волны у бэкенда
      bin.forEach(x => {
        send({ action: 'resolve_duration', path: x.path, seg_id: x.id, field: 'bin' });
      });
      imported.forEach(s => {
        const aPath = s.audio || s.video;
        if (aPath) send({ action: 'get_waveform', path: aPath });
      });

      showFirstFrameRef.current = true;
    } catch (err: any) {
      alert('Ошибка чтения JSON: ' + (err?.message || err));
    } finally {
      if (fileImportRef.current) fileImportRef.current.value = '';
    }
  }, [send]);

  const displayBlocks = useMemo<DisplayBlock[]>(() => {
    const blocks: DisplayBlock[] = [];
    clips.forEach(c => {
      const last = blocks[blocks.length - 1];
      const seg = segments.find(s => s.id === c.segment_id);
      const lbl = c.segment_id === null ? 'Интро' : (seg?.video.split('/').pop() || 'Слайды');
      const bStart = c.global_start, bEnd = c.global_start + c.duration;
      const segHasMask = Boolean(seg && getSegmentMasks(seg).some(m => {
        if (!m.image) return false;
        const mS = parseTime(m.start), mE = parseTime(m.end || '99:99:99');
        return Math.max(bStart, mS) < Math.min(bEnd, mE);
      }));
      if (last && last.segId === c.segment_id && c.segment_id !== null && last.hasOverlay === c.has_overlay && last.hasMask === segHasMask && (!c.has_overlay || last.label === lbl)) {
        last.duration += c.duration;
      } else {
        blocks.push({
          key: `${c.segment_id ?? 'intro'}-${blocks.length}`,
          segId: c.segment_id, globalStart: c.global_start, duration: c.duration, sourceStart: c.source_start ?? 0,
          hasOverlay: c.has_overlay, hasMask: segHasMask, label: lbl, overlaySource: c.overlay_source, overlaySourceStart: c.overlay_source_start,
        });
      }
    });
    return blocks;
  }, [clips, segments]);

  const activePip = useMemo(() => {
    const idx = segments.findIndex(s => s.id === activePipSegId);
    const seg = segments[idx];
    if (!seg) return null;
    const curOvs = getSegmentOverlays(seg);
    const activeOv = curOvs.find(o => {
      const sSec = parseTime(o.timeline_start || seg.start || '00:00:00');
      const eSec = parseTime(o.timeline_end || seg.end || '99:99:99');
      return currentTime >= sSec - 0.1 && currentTime < eSec + 0.1;
    }) || curOvs[0];
    return activeOv ? { segId: seg.id, segmentNumber: idx + 1, overlay: activeOv } : null;
  }, [segments, activePipSegId, currentTime]);

  const handlePipSnap = useCallback((corner: PipCorner) => {
    if (!activePip) return;
    const camBin = mediaBin.find(x => x.path === activePip.overlay.video);
    const coords = getPipSnapCoords(corner, activePip.overlay.w || PIP_DEFAULT_W, camBin?.width || 1920, camBin?.height || 1080, settings.canvasWidth, settings.canvasHeight, activePip.overlay.crop);
    setSegments(prev => prev.map(s => {
      if (s.id !== activePip.segId) return s;
      const updated = getSegmentOverlays(s).map(o => (o.id === activePip.overlay.id ? { ...o, ...coords } : o));
      return { ...s, overlays: updated, overlay: updated.find(o => o.id === activePip.overlay.id) || { ...activePip.overlay, ...coords } };
    }));
  }, [activePip, mediaBin, settings.canvasWidth, settings.canvasHeight]);

  const selectedSeg = segments.find(s => s.id === selectedSegId) ?? segments[0] ?? null;
  const startBlockReason = !settings.outputDir.trim() ? 'Не выбрана папка сохранения' : !segments.some(s => s.video) ? 'Добавьте фрагмент с видео' : null;

  const segmentCanvas = useCallback((s: Segment): { width: number; height: number } | null => {
    const item = mediaBin.find(x => x.path === s.video);
    if (!item?.width || !item?.height) return null;
    const c = s.crop;
    if (!c || !(c.top || c.bottom || c.left || c.right)) return { width: item.width, height: item.height };
    return fitCanvasSize(Math.max(16, item.width - (c.left || 0) - (c.right || 0)), Math.max(16, item.height - (c.top || 0) - (c.bottom || 0)));
  }, [mediaBin]);

  const canvasSuggestion = useMemo(() => {
    for (const s of segments) {
      if (!s.video) continue;
      const item = mediaBin.find(x => x.path === s.video), want = segmentCanvas(s);
      if (want && (want.width !== settings.canvasWidth || want.height !== settings.canvasHeight || Math.abs((item?.fps || 0) - settings.canvasFps) > 0.5)) {
        return { width: want.width, height: want.height, fps: item?.fps || 0 };
      }
    }
    return null;
  }, [segments, mediaBin, segmentCanvas, settings.canvasWidth, settings.canvasHeight, settings.canvasFps]);

  const finishCropping = () => {
    setIsCropping(false);
    const seg = segments.find(s => s.id === selectedSegId) ?? segments[0];
    const want = seg?.crop && (seg.crop.top || seg.crop.bottom || seg.crop.left || seg.crop.right) ? segmentCanvas(seg) : null;
    if (want) setSettings(s => ({ ...s, canvasWidth: want.width, canvasHeight: want.height }));
  };
  const canvasSuggestionSig = canvasSuggestion ? `${canvasSuggestion.width}x${canvasSuggestion.height}@${Math.round(canvasSuggestion.fps)}` : null;

  useEffect(() => {
    if (!hasAutoAppliedCanvasRef.current && canvasSuggestion) {
      hasAutoAppliedCanvasRef.current = true;
      setSettings(s => ({ ...s, canvasWidth: canvasSuggestion.width, canvasHeight: canvasSuggestion.height, canvasFps: canvasSuggestion.fps ? Math.round(canvasSuggestion.fps * 100) / 100 : s.canvasFps }));
    }
  }, [canvasSuggestion]);

  // Маски, активные в текущий момент времени
  const currentPlayingSeg = useMemo(() => {
    const curClip = clips.find(c => currentTime >= c.global_start && currentTime < c.global_start + c.duration);
    if (curClip?.segment_id) {
      return segments.find(s => s.id === curClip.segment_id) || selectedSeg;
    }
    return selectedSeg;
  }, [clips, currentTime, segments, selectedSeg]);

  const allVisibleMasks = useMemo(() => {
    const seg = isMasking ? selectedSeg : currentPlayingSeg;
    if (!seg) return [];
    const { globalStart, segStart } = getSegmentOffsets(seg.id, segments, displayBlocks);
    const segCurrentTime = segStart + Math.max(0, currentTime - globalStart);
    return getSegmentMasks(seg).filter(m => {
      const s = parseTime(m.start);
      const e = parseTime(m.end || '99:99:99');
      return segCurrentTime >= s - 0.05 && segCurrentTime <= e + 0.05;
    });
  }, [isMasking, selectedSeg, currentPlayingSeg, currentTime, segments, displayBlocks]);

  const activeMask = useMemo(() => {
    if (!selectedSeg || !activeMaskId) return null;
    return getSegmentMasks(selectedSeg).find(m => m.id === activeMaskId) || null;
  }, [selectedSeg, activeMaskId]);

  const handleToggleMasking = useCallback((maskId?: string) => {
    if (maskId) {
      setActiveMaskId(maskId);
      setIsMasking(true);
      const seg = segments.find(s => s.id === selectedSegId) ?? segments[0];
      const m = seg ? getSegmentMasks(seg).find(x => x.id === maskId) : null;
      if (m && m.start) {
        const sSec = parseTime(m.start);
        if (currentTime < sSec || currentTime > parseTime(m.end || '99:99:99')) {
          streamer.seek(sSec);
        }
      }
    } else {
      setIsMasking(false);
      setActiveMaskId(null);
    }
  }, [segments, selectedSegId, currentTime, streamer]);

  const handleAutoDetectSlide = useCallback((maskId?: string) => {
    const mId = maskId || activeMaskId;
    const seg = selectedSeg;
    if (!seg || !seg.video || !mId) return;

    const { globalStart, segStart } = getSegmentOffsets(seg.id, segments, displayBlocks);
    const segCurrentTime = segStart + Math.max(0, currentTime - globalStart);
    const segEndSec = seg.end ? parseTime(seg.end) : 0;

    setDetectingMaskId(mId);
    send({
      action: 'detect_slide_bounds',
      video_path: seg.video,
      seg_id: seg.id,
      mask_id: mId,
      current_time: segCurrentTime,
      seg_start: segStart,
      seg_end: segEndSec > 0 ? segEndSec : null,
      crop: seg.crop,
    });
  }, [activeMaskId, selectedSeg, segments, displayBlocks, currentTime, send]);

  const handleSaveMaskImage = useCallback((maskId: string, image: string, color?: string, brushSize?: number) => {
    if (!selectedSegId) return;
    setSegments(prev => prev.map(s => {
      if (s.id !== selectedSegId) return s;
      const msks = getSegmentMasks(s).map(m => m.id === maskId ? { ...m, image, color: color ?? m.color, brushSize: brushSize ?? m.brushSize } : m);
      return { ...s, masks: msks };
    }));
  }, [selectedSegId]);

  return (
    <Box maw={1800} mx="auto" px="xl" py="lg">
      <input type="file" ref={fileImportRef} accept=".json,application/json" style={{ display: 'none' }} onChange={handleImport} />
      <Flex gap="xl" wrap="nowrap" align="flex-start">
        <Box style={{ flex: '0 0 480px', width: 480, minWidth: 320 }}>
          <Box mb="lg">
            <MediaBin items={mediaBin} onAddFile={() => { setBrowseTarget({ type: 'bin' }); browse('source'); }} onRemoveItem={id => setMediaBin(prev => prev.filter(x => x.id !== id))} onAppendAsSegment={item => addSegmentFromBin(item.path)} />
          </Box>
          {canvasSuggestion && canvasSuggestionSig !== canvasWarnDismissed && (
            <Alert color="yellow" mb="md" p="xs" withCloseButton onClose={() => setCanvasWarnDismissed(canvasSuggestionSig)}>
              <Text fz="sm" mb={6}>Источник ({canvasSuggestion.width}x{canvasSuggestion.height}) не совпадает с холстом ({settings.canvasWidth}x{settings.canvasHeight}).</Text>
              <Button size="compact-xs" variant="light" color="yellow" onClick={() => setSettings(s => ({ ...s, canvasWidth: canvasSuggestion.width, canvasHeight: canvasSuggestion.height, canvasFps: canvasSuggestion.fps ? Math.round(canvasSuggestion.fps * 100) / 100 : s.canvasFps }))}>Настроить холст под источник</Button>
            </Alert>
          )}
          {selectedSeg ? (
            <Box mb="lg">
              <SegmentCard
                canvasWidth={settings.canvasWidth}
                canvasHeight={settings.canvasHeight}
                currentTime={currentTime}
                isMasking={isMasking}
                activeMaskId={activeMaskId}
                onToggleMasking={handleToggleMasking}
                detectingMaskId={detectingMaskId}
                onAutoDetectSlide={handleAutoDetectSlide}
                seg={selectedSeg} mediaBin={mediaBin} onUpdate={patch => updateSegment(selectedSeg.id, patch)} onRemove={() => removeSegment(selectedSeg.id)}
                onBrowseVideo={() => { setBrowseTarget({ type: 'video', segId: selectedSeg.id }); browse('source'); }}
                onBrowseOverlay={() => { setBrowseTarget({ type: 'overlay', segId: selectedSeg.id }); browse('source'); }}
                onSelectVideoFromBin={path => updateSegment(selectedSeg.id, { video: path, audio: path })}
                onBrowseAudio={() => { setBrowseTarget({ type: 'audio', segId: selectedSeg.id }); browse('source', undefined, 'audio'); }}
                onAutoSync={(...args: any[]) => send({ action: 'auto_sync_audio', master_path: args[0], sample_path: args[1], seg_id: args[2], ov_id: args[3] })}
                onAutoColorGrade={(vPath, sId, oId, cr) => send({ action: 'auto_color_grade', video_path: vPath, seg_id: sId, ov_id: oId, crop: cr })}
                isCropping={isCropping} onToggleCropping={() => (isCropping ? finishCropping() : setIsCropping(true))}
                onFitCanvas={(cw, ch) => { const fit = fitCanvasSize(cw, ch); setSettings(s => ({ ...s, canvasWidth: fit.width, canvasHeight: fit.height })); }}
              />
            </Box>
          ) : <Text c="dimmed" fz="sm" ta="center" mb="lg">Добавьте видео из корзины проекта.</Text>}

          <EditorSettings
            settings={settings} onSettingsChange={patch => setSettings(s => ({ ...s, ...patch }))}
            onBrowseOutput={() => { setBrowseTarget({ type: 'output' }); browse('output'); }}
            mediaBin={mediaBin} onCanvasFromBin={item => setSettings(s => ({ ...s, canvasWidth: item.width ?? s.canvasWidth, canvasHeight: item.height ?? s.canvasHeight, canvasFps: item.fps ? Math.round(item.fps * 100) / 100 : s.canvasFps }))}
            onExport={handleExport} onImport={() => fileImportRef.current?.click()} hwCapabilities={hwCapabilities} modalOpened={settingsOpen} onCloseModal={() => setSettingsOpen(false)}
          />

          <Group gap="xs" wrap="nowrap">
            <Button
              style={{ flex: 1 }} size="md" color={isProcessing ? 'red' : 'blue'} loading={isCancelling} disabled={!isProcessing && startBlockReason !== null}
              onClick={() => {
                if (isProcessing) { setIsCancelling(true); send({ action: 'cancel' }); return; }
                if (startBlockReason) return;
                setIsProcessing(true); setLogEntries([]); setOverallProgress({ rendered: '00:00:00', total: formatTime(totalDuration), percent: 0 }); send({ action: 'process', params: gatherParams() });
              }}
            >
              {isProcessing ? 'Отменить' : 'Начать обработку'}
            </Button>
            <Tooltip label="Параметры" withArrow><ActionIcon size="xl" variant="light" onClick={() => setSettingsOpen(true)}><Ellipsis size={18} /></ActionIcon></Tooltip>
          </Group>
          {!isProcessing && startBlockReason && <Text c="red" fz="sm" mt={6}>{startBlockReason}</Text>}
        </Box>

        <Box style={{ flex: '1 1 500px', minWidth: 0 }}>
          <VideoPlayer
            videoRef={videoRef} pipVideoRef={pipVideoRef} isLoading={streamer.isLoading} activePip={activePip} onPipSnap={handlePipSnap}
            canvasWidth={settings.canvasWidth} canvasHeight={settings.canvasHeight}
            camWidth={mediaBin.find(x => x.path === activePip?.overlay.video)?.width || 1920}
            camHeight={mediaBin.find(x => x.path === activePip?.overlay.video)?.height || 1080}
            isCropping={isCropping} onCloseCrop={finishCropping}
            activeCrop={selectedSeg ? { crop: selectedSeg.crop || { top: 0, bottom: 0, left: 0, right: 0 }, sourceWidth: mediaBin.find(x => x.path === selectedSeg.video)?.width || 1920, sourceHeight: mediaBin.find(x => x.path === selectedSeg.video)?.height || 1080 } : null}
            onCropChange={newCrop => { if (selectedSeg) updateSegment(selectedSeg.id, { crop: newCrop }); }}
            onPipFinish={(x, y, w) => {
              if (activePip) {
                setSegments(prev => prev.map(s => {
                  if (s.id !== activePip.segId) return s;
                  const curOvs = getSegmentOverlays(s);
                  const updated = curOvs.map(o => o.id === activePip.overlay.id ? { ...o, x, y, w } : o);
                  return { ...s, overlays: updated, overlay: updated.find(o => o.id === activePip.overlay.id) || { ...activePip.overlay, x, y, w } };
                }));
              }
            }}
            isMasking={isMasking}
            activeMask={activeMask}
            allVisibleMasks={allVisibleMasks}
            onCloseMasking={() => handleToggleMasking(undefined)}
            onSaveMaskImage={handleSaveMaskImage}
            isDetectingSlide={detectingMaskId !== null}
            onAutoDetectSlide={() => handleAutoDetectSlide()}
          />

          {previewVisible && (
            <Timeline
              waveforms={waveforms} silenceMap={silenceMap} defaultIntro={defaultIntro} globalVolume={settings.volume} displayBlocks={displayBlocks}
              segments={segments} currentTime={currentTime} totalDuration={totalDuration}
              isPlaying={streamer.isPlaying} selectedSegId={selectedSegId} onTogglePlay={streamer.togglePlay}
              onSeek={(t, r) => streamer.seek(t, r)} onTimeChange={setCurrentTime} onSelectSegment={setSelectedSegId}
              onSplit={splitSegment} onSplitAtCursor={splitAtPlayhead} onRemove={removeSegment}
              onToggleFade={(id, field) => updateSegment(id, { [field]: !segments.find(s => s.id === id)?.[field] })}
              onReorder={(fromId, toId) => { const from = segments.findIndex(s => s.id === fromId); const to = segments.findIndex(s => s.id === toId); if (from >= 0 && to >= 0) reorderSegment(from, to); }}
              onAppendFromBin={addSegmentFromBin}
              onSelectOverlay={segId => setSelectedSegId(segId)}
              onSelectMask={(segId, maskId) => {
                setSelectedSegId(segId);
                handleToggleMasking(maskId);
              }}
            />
          )}

          <LogPanel logEntries={logEntries} logLevel={logLevel} onLogLevelChange={setLogLevel} onClearLogs={() => setLogEntries([])} overallProgress={overallProgress} />
        </Box>
      </Flex>

      <FilePickerModal
        open={browseTarget !== null} browseResult={browseResult} showDirConfirm={browseTarget?.type === 'output'} onClose={() => setBrowseTarget(null)}
        onBrowse={path => browseTarget && browse(browseTarget.type === 'output' ? 'output' : 'source', path, browseTarget.type === 'audio' ? 'audio' : undefined)}
        onSelect={path => {
          if (!browseTarget) return;
          if (browseTarget.type === 'output') { setSettings(s => ({ ...s, outputDir: path })); setBrowseTarget(null); return; }
          send({ action: 'resolve_path', share: 'source', path });
        }}
        onCreateFolder={(path, name) => { browseShareRef.current = 'output'; send({ action: 'create_folder', path, name }); }}
      />
    </Box>
  );
}
